import clsx from 'clsx';
import React, { type ComponentPropsWithoutRef, forwardRef, useCallback, useMemo } from 'react';
import { mergeProps } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';
import { useSafeContext } from '@gitbook/util-react-hooks';

import { Button, type ButtonHookProps, type LinkHookProps, useButton, useLink } from '../Button';
import { Tooltip } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';

export type BreadcrumbsProps = {
    /**
     * The children of the breadcrumb
     *
     * @required
     */
    children: React.ReactNode;
    /**
     * A non-visual label exposed to screen readers and used to distinguish the
     * landmark from other nav landmarks.
     *
     * @default "Breadcrumbs"
     */
    label?: string;

    className?: string;
};

export function Breadcrumbs(props: BreadcrumbsProps) {
    const { children, label = 'Breadcrumbs', className } = props;
    const items = React.Children.toArray(children);
    // Track the registered items to determine the current item and the item count.
    // Analyzing the children alone can be misleading because of the conditional rendering.
    const [registered, setRegistered] = React.useState<number[]>([]);
    return (
        <nav className={clsx('flex items-center', className)} aria-label={label}>
            <ol className="flex gap-1">
                {items.map((item, index) => (
                    <BreadcrumbItemProvider
                        key={index}
                        index={index}
                        itemsCount={registered.length}
                        resolvedIndex={registered.indexOf(index)}
                        setRegistered={setRegistered}
                    >
                        {item}
                    </BreadcrumbItemProvider>
                ))}
            </ol>
        </nav>
    );
}

function BreadcrumbItemProvider(props: {
    children: React.ReactNode;
    index: number;
    itemsCount: number;
    resolvedIndex: number;
    setRegistered: React.Dispatch<React.SetStateAction<number[]>>;
}) {
    const { children, index, resolvedIndex, itemsCount, setRegistered } = props;
    const register = useCallback(() => {
        setRegistered((prev) => {
            if (!prev.includes(index)) {
                return [...prev, index].sort();
            }
            return prev;
        });
        return () => {
            setRegistered((prev) => {
                if (prev.includes(index)) {
                    return prev.filter((i) => i !== index);
                }
                return prev;
            });
        };
    }, [index, setRegistered]);
    const value = useMemo(() => {
        return { index: resolvedIndex, itemsCount, register };
    }, [resolvedIndex, itemsCount, register]);
    return (
        <BreadcrumbItemContext.Provider value={value}>{children}</BreadcrumbItemContext.Provider>
    );
}

type BreadcrumbItemContextType = {
    index: number;
    itemsCount: number;
    register: () => () => void;
};

const BreadcrumbItemContext = React.createContext<BreadcrumbItemContextType | null>(null);

export type BreadcrumbItemProps = {
    label?: string;
    children?: React.ReactNode | ((props: { isCurrent: boolean }) => React.ReactNode);
    icon?: IconDefinition;
    hoverIcon?: IconDefinition;
    iconOnly?: boolean;
    /**
     * Set a unique ID that can be used for testing purposes.
     */
    testID?: string;
    className?: string;
} & Pick<BreadcrumbLinkProps, 'href' | 'routerOptions'>;

export function BreadcrumbItem(props: BreadcrumbItemProps) {
    const { label, icon, hoverIcon, iconOnly, href, routerOptions, testID, className } = props;

    const ctx = useSafeContext(
        BreadcrumbItemContext,
        `BreadcrumbItem component requires "BreadcrumbItemContext"`
    );
    const { index, itemsCount, register } = ctx;
    const isFirst = index === 0;
    const isBeforePrevious = index < itemsCount - 2;
    const isPrevious = index === itemsCount - 2;
    const isCurrent = index === itemsCount - 1;

    React.useLayoutEffect(() => register(), [register]);

    const children = props.children ?? label;

    return (
        <li
            className={clsx(
                'group flex items-center gap-1 text-ui-action',
                iconOnly && 'shrink-0',
                isBeforePrevious && 'max-lg:hidden',
                className
            )}
            data-testid={testID}
        >
            {!isFirst && <BreadcrumbSeparator />}

            {typeof children === 'function' ? (
                children({ isCurrent })
            ) : isCurrent || !href ? (
                <BreadcrumbSpan
                    isCurrent={isCurrent}
                    iconOnly={iconOnly}
                    label={iconOnly ? label : undefined}
                    icon={icon}
                    hoverIcon={hoverIcon}
                >
                    {iconOnly ? null : children}
                </BreadcrumbSpan>
            ) : (
                <BreadcrumbLink
                    href={href}
                    routerOptions={routerOptions}
                    label={iconOnly ? label : undefined}
                    data-previous={isPrevious ? 'true' : undefined}
                    iconOnly={iconOnly}
                >
                    <BreadcrumbIcon
                        icon={icon || isPrevious ? <ChevronLeftIconOnMobile icon={icon} /> : icon}
                        hoverIcon={hoverIcon}
                    />

                    {iconOnly ? null : typeof children === 'string' ? (
                        <span className={clsx('truncate', isPrevious && 'max-lg:hidden')}>
                            {children}
                        </span>
                    ) : (
                        <span className={clsx(isPrevious && 'max-lg:hidden')}>{children}</span>
                    )}
                </BreadcrumbLink>
            )}
        </li>
    );
}

export function BreadcrumbSeparator() {
    return (
        <span aria-hidden className="shrink-0 select-none text-light max-lg:hidden">
            /
        </span>
    );
}

function BreadcrumbIcon(props: {
    icon: IconDefinition | undefined;
    hoverIcon: IconDefinition | undefined;
}) {
    const { icon, hoverIcon } = props;
    if (!icon) {
        return null;
    }

    const iconElement = renderIcon(icon, { size: 18, className: 'shrink-0' });

    if (hoverIcon) {
        return (
            <>
                <div
                    className={clsx(
                        'pointer-events-none absolute flex bg-base transition',
                        'group-hover/breadcrumb-item:opacity-0 peer-focus:opacity-0 group-data-[focus-visible]/breadcrumb-item:opacity-0',
                        'group-aria-expanded/breadcrumb-item:opacity-0'
                    )}
                >
                    {iconElement}
                </div>
                {renderIcon(hoverIcon, { size: 18, className: 'shrink-0' })}
            </>
        );
    }

    return iconElement;
}

function ChevronLeftIconOnMobile(props: {
    icon: IconDefinition | undefined;
    size?: number;
    className?: string;
}) {
    const { icon } = props;
    return (
        <>
            {icon &&
                renderIcon(icon, {
                    size: props.size,
                    className: clsx(props.className, 'max-lg:hidden'),
                })}
            <Icon.ChevronLeft size={props.size} className={clsx(props.className, 'lg:hidden')} />
        </>
    );
}

function getBreadcrumbPressableClassName(args: {
    iconOnly: boolean;
    isDisabled: boolean;
    isFocusVisible: boolean;
    isActive: boolean;
}) {
    const { iconOnly, isDisabled, isFocusVisible, isActive } = args;
    return clsx(
        'group/breadcrumb-item flex items-center whitespace-nowrap',
        'rounded-md bg-base hover:bg-standout-base hover:text-base aria-expanded:bg-standout-base aria-expanded:text-base',
        isActive ? 'text-base' : 'text-muted',
        iconOnly ? 'p-1' : 'gap-2 p-1',
        isFocusVisible && 'shadow-focus-ring',
        isDisabled && 'opacity-disabled'
    );
}

type BreadcrumbLinkProps = LinkHookProps & { label?: string; iconOnly?: boolean } & Omit<
        ComponentPropsWithoutRef<'a'>,
        'href'
    >;

function BreadcrumbLink(props: BreadcrumbLinkProps) {
    const { label, iconOnly = false, ...rest } = props;
    const { linkProps, isDisabled, isFocusVisible } = useLink(props);
    return (
        <Tooltip label={label}>
            <a
                {...mergeProps(rest, linkProps)}
                className={clsx(
                    getBreadcrumbPressableClassName({
                        iconOnly,
                        isDisabled,
                        isFocusVisible,
                        isActive: false,
                    }),
                    props.className
                )}
            />
        </Tooltip>
    );
}

export interface BreadcrumbButtonProps
    extends ComponentPropsWithoutRef<'button'>,
        Omit<ButtonHookProps, 'elementType'> {
    label: string;
    iconOnly?: boolean;
    icon?: IconDefinition;
    hoverIcon?: IconDefinition;
    isActive: boolean;
}

export const BreadcrumbButton = forwardRef(function BreadcrumbButton(
    props: BreadcrumbButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    const {
        label,
        iconOnly = false,
        icon,
        hoverIcon,
        onPress,
        preventFocusOnPress,
        onPressStart,
        isActive,
        ...rest
    } = props;
    const { buttonProps, isDisabled, isFocusVisible } = useButton({
        onPress,
        preventFocusOnPress,
        onPressStart,
        elementType: 'button',
    });

    return (
        <Button
            ref={ref}
            label={label}
            kind="blank"
            size="small"
            iconOnly={iconOnly}
            leadingIcon={<BreadcrumbIcon icon={icon} hoverIcon={hoverIcon} />}
            {...mergeProps(rest, buttonProps)}
            className={clsx(
                getBreadcrumbPressableClassName({
                    iconOnly,
                    isDisabled,
                    isFocusVisible,
                    isActive,
                }),
                props.className
            )}
        />
    );
});

export function BreadcrumbSpan(
    props: React.PropsWithChildren<{
        label?: string;
        isCurrent: boolean;
        iconOnly?: boolean;
        icon?: IconDefinition;
        hoverIcon?: IconDefinition;
    }>
) {
    const { isCurrent, icon, hoverIcon, iconOnly, children, label } = props;

    return (
        <Tooltip label={label}>
            <span
                role="link"
                aria-current={isCurrent ? 'page' : undefined}
                className={clsx(
                    'group/breadcrumb-item flex cursor-default items-center gap-2 whitespace-nowrap px-1 group-last:pr-0',
                    isCurrent ? 'text-base' : 'text-muted'
                )}
            >
                <BreadcrumbIcon icon={icon} hoverIcon={hoverIcon} />

                {iconOnly ? null : typeof children === 'string' ? (
                    <span className="truncate">{children}</span>
                ) : (
                    <span>{children}</span>
                )}
            </span>
        </Tooltip>
    );
}
